<template>
  <div ref="myCollectionRoot" :style="pageStyle">
    <div class="contentPage listPage">
      <div
        v-if="groupedList && groupedList.length"
        class="collectionCategoryList">
        <div
          v-for="group in groupedList"
          :key="group.categoryId"
          class="collectionCategoryGroup"
          :data-category-id="group.categoryId"
          :style="{paddingTop: pageFrame}">
          <div
            v-if="group.items && group.items.length"
            class="collectionItemList"
            :style="listStyle">
            <CollectionItem
              v-for="item in group.items"
              :key="item.collectionItemId"
              :locale="locale"
              :categoryId="categoryId"
              :lang="lang"
              :item="item"
              :data-item="item.collectionItemId"
              :collectionIcon="collectionIcon"
              :itemDisplay="itemDisplay"
              @deleteItem="deleteItem" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CollectionItem from './MyCollectionItem'

export default {
  name: 'MyCollection',
  props: [
    'locale',
    'lang',
    'pageId',
    'categoryId'
  ],
  components: {
    CollectionItem
  },
  computed: {
    getMyCollectionSetting () {
      return this.$store.getters['collection/getMyCollectionSetting']
    },
    pageFrame () {
      const frame = this.$store.getters['collection/getPageFrame']
      return frame ? `${frame}px` : ''
    },
    pageStyle () {
      const frame = this.pageFrame
      return {
        padding: frame,
        paddingTop: '0'
      }
    },
    listStyle () {
      const setting = this.getMyCollectionSetting
      return {
        gridTemplateColumns: `repeat(${setting.listColumn}, 1fr)`
      }
    },
    itemDisplay () {
      const setting = this.getMyCollectionSetting
      return setting && setting.display
    },
    collectionIcon () {
      const myCollectionSetting = this.$store.getters['collection/getMyCollectionSetting']
      const scrapIcon = myCollectionSetting && {
        ...myCollectionSetting.scrapIcon,
        size: `${myCollectionSetting.scrapIcon.size}px`
      }
      return scrapIcon
    }
  },
  
  created () {
    this.init()
  },
  watch: {
    categoryId (categoryId) {
      this.scrollToCategory(categoryId)
    }
  },
  beforeDestroy () {
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }

    if (this.scrollElement) {
      this.scrollElement.removeEventListener('scroll', this.handleScroll)
    }
  },
  mounted () {
    // 10초(10000ms)마다 호출
    this.intervalId = setInterval(() => {
      this.getMyCollectionItems()
    }, this.intervalDur)
    this.$nextTick(() => {
      const root = this.$refs.myCollectionRoot
      const scrollEl = root && root.closest('.collectionContent')
      if (scrollEl) {
        this.scrollElement = scrollEl
        scrollEl.addEventListener('scroll', this.handleScroll)
      }
    })
  },
  data () {
    return {
      intervalDur: 10000,
      intervalId: null,
      groupedList: [],
      scrollElement: null,
      isProgrammaticScroll: false
    }
  },
  methods: {
    init () {
      const category = this.getMyCollectionSetting && this.getMyCollectionSetting.category
      const myCollectionCategoryId = getFirstCategoryId(category)
      function getFirstCategoryId (arr) {
        if (!arr || arr.length === 0) return null
        const first = arr[0]
        if (first.subCategory && first.subCategory && first.subCategory.length > 0) {
          return first.subCategory[0].categoryId
        }
        return first.categoryId
      }
      if (!this.categoryId) {
        if (myCollectionCategoryId) {
          this.$router.replace({ params: {
            ...this.$route.params,
            categoryId: myCollectionCategoryId
          } })
        }
      }
      this.getMyCollectionItems(myCollectionCategoryId)
    },
    handleScroll () {
      if (this.isProgrammaticScroll) return

      const root = this.$refs.myCollectionRoot
      const scrollEl = this.scrollElement

      if (!root || !scrollEl) return

      const groups = [...root.querySelectorAll('[data-category-id]')]
      if (!groups.length) return

      // 최상단이면 무조건 첫 번째 유효 leaf categoryId
      const isTop = scrollEl.scrollTop <= 2

      if (isTop) {
        const firstGroup = groups[0]
        const firstCategoryId = firstGroup && firstGroup.dataset.categoryId

        if (
          firstCategoryId &&
          firstCategoryId !== this.$route.params.categoryId
        ) {
          this.$router.replace({
            params: {
              ...this.$route.params,
              categoryId: firstCategoryId
            }
          })
        }

        return
      }

      // 최하단이면 무조건 마지막 유효 leaf categoryId
      const isBottom =
        scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 2

      if (isBottom) {
        const lastGroup = groups[groups.length - 1]
        const lastCategoryId = lastGroup && lastGroup.dataset.categoryId

        if (
          lastCategoryId &&
          lastCategoryId !== this.$route.params.categoryId
        ) {
          this.$router.replace({
            params: {
              ...this.$route.params,
              categoryId: lastCategoryId
            }
          })
        }

        return
      }

      const scrollRect = scrollEl.getBoundingClientRect()

      let activeCategoryId = null
      let minDistance = Infinity

      groups.forEach(group => {
        const groupRect = group.getBoundingClientRect()
        const distance = Math.abs(groupRect.top - scrollRect.top)

        if (distance < minDistance) {
          minDistance = distance
          activeCategoryId = group.dataset.categoryId
        }
      })

      if (
        activeCategoryId &&
        activeCategoryId !== this.$route.params.categoryId
      ) {
        this.$router.replace({
          params: {
            ...this.$route.params,
            categoryId: activeCategoryId
          }
        })
      }
    },
    async getMyCollectionItems (myCollectionCategoryId) {
      const collectionCategory = this.categoryId || myCollectionCategoryId
      const getItemProperty = this.$store.getters['collection/getItemByItemId']

      const res = await this.$store.dispatch('collection/getMyCollectionItems', {
        pageId: this.pageId,
        collectionCategory
      })

      const list = res && res.message
      const categories = this.getMyCollectionSetting && this.getMyCollectionSetting.category

      function flattenLeafCategories (categories) {
        return (categories || []).reduce((acc, category) => {
          if (category.subCategory && category.subCategory.length > 0) {
            return acc.concat(flattenLeafCategories(category.subCategory))
          }

          acc.push(category)
          return acc
        }, [])
      }

      function groupItemsByCategory (items, categories) {
        const flatCategories = flattenLeafCategories(categories)

        return flatCategories
          .map(category => {
            return {
              categoryId: category.categoryId,
              categoryLabel: category.categoryLabel,
              items: items.filter(item => item.collectionCategoryId === category.categoryId)
            }
          })
          .filter(group => group.items.length > 0)
      }

      if (list) {
        const mappedItems = list
          .filter(item => item && getItemProperty(item.collectionItemId))
          .map(item => getItemProperty(item.collectionItemId))

        this.groupedList = groupItemsByCategory(mappedItems, categories)
        this.scrollToCategory(collectionCategory)
      } else {
        this.groupedList = []
      }
    },
    async deleteItem (collectionItemId) {
      const data = {
        pageId: this.pageId,
        collectionCategory: this.categoryId,
        orderItemId: collectionItemId
      }
      this.$store.commit('loading', true)
      const res = await this.$store.dispatch('collection/removeMyCollectionItem', data)
      if (res && res.status === 200) {
        this.$store.commit('collection/setToastMessage', '내 콜렉션에서 삭제되었습니다.')
        this.getMyCollectionItems()
      } else {
        alert(res)
      }
      this.$store.commit('loading', false)
    },
    scrollToCategory (categoryId) {
      this.$nextTick(() => {
        const root = this.$refs.myCollectionRoot
        const scrollEl = this.scrollElement || (root && root.closest('.collectionContent'))

        if (!root || !scrollEl) return

        const target = root.querySelector(`[data-category-id="${categoryId}"]`)
        if (!target) return

        const scrollTop =
          target.getBoundingClientRect().top -
          scrollEl.getBoundingClientRect().top +
          scrollEl.scrollTop

        this.isProgrammaticScroll = true

        scrollEl.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        })

        setTimeout(() => {
          this.isProgrammaticScroll = false
        }, 500)
      })
    }
  }
}
</script>